export type Clinician = {
  id: string
  name: string
  credentials: string
  location: string
  portrait: string
  challenge: string
  hook: string
  cta: string
  interaction: string
  tease: string
}

export const clinicians: Clinician[] = [
  {
    id: 'oconnor',
    name: "Dr Chris O'Connor",
    credentials: 'Specialty [TBC]',
    location: 'Case 1',
    portrait: 'https://cdn.magicpatterns.com/uploads/onKGFV1qNJScHXU6dAZX2N/OConnor.jpg',
    challenge:
      'Clinical treasure chest — restoring extensive root caries with Filtek™ Easy Match Flowable Restorative.',
    hook: 'Would you have made the same restorative decision?',
    cta: 'See the complete clinical case',
    interaction: 'What would influence your approach most?',
    tease: "Dr O'Connor chose a different approach.",
  },
  {
    id: 'dobbertin',
    name: 'Dr Maximilian Dobbertin',
    credentials: 'Specialty [TBC]',
    location: 'Case 2',
    portrait: 'https://cdn.magicpatterns.com/uploads/kHVC2EMV5gmKorep786Tc4/Maximilian.jpg',
    challenge: 'Case 2 of the Filtek clinical case series — clinical detail [TBC].',
    hook: 'How would you approach this case?',
    cta: 'Scan the case',
    interaction: 'Which factor would decide your technique?',
    tease: 'The outcome was not the obvious one.',
  },
  {
    id: 'thering',
    name: 'Dr Silvana Thering',
    credentials: 'Specialty [TBC]',
    location: 'Case 3',
    portrait: 'https://cdn.magicpatterns.com/uploads/sPe5b97H6ZWepuqjHV6D4x/Silvana.jpg',
    challenge: 'Case 3 of the Filtek clinical case series — clinical detail [TBC].',
    hook: 'One patient. One clinical challenge. What would you do next?',
    cta: 'See how she solved this case',
    interaction: 'How would you sequence this restoration?',
    tease: 'One step earlier than most clinicians expect.',
  },
  {
    id: 'bonacina',
    name: 'Dr Cristiano Bonacina',
    credentials: 'Specialty [TBC]',
    location: 'Case 4',
    portrait: 'https://cdn.magicpatterns.com/uploads/uuCf7UNkYdwsRmPNuVKHYq/Cristiano.jpg',
    challenge: 'Case 4 of the Filtek clinical case series — clinical detail [TBC].',
    hook: 'What would you do next?',
    cta: 'See how Dr Bonacina solved this case',
    interaction: 'Which material decision would you make first?',
    tease: 'One decision changed the whole sequence.',
  },
]

export const caseImage =
  'https://cdn.magicpatterns.com/patterns/generated-images/ff2585f3-f587-41b9-8f88-bccd38467028.jpg'

export type TeaserScreen = {
  step: string
  title: string
  note: string
}

export const teaserScreens: TeaserScreen[] = [
  {
    step: 'Screen 1',
    title: 'Face + hook',
    note: 'The clinician appears first. One question, no product message.',
  },
  {
    step: 'Screen 2',
    title: 'Clinical situation',
    note: 'One image and one very short description of the case.',
  },
  {
    step: 'Screen 3',
    title: 'Interaction',
    note: 'A quiz, poll or clinical decision the HCP commits to.',
  },
  {
    step: 'Screen 4',
    title: 'Reveal / tease',
    note: 'One surprising insight — never the full resolution.',
  },
  {
    step: 'Screen 5',
    title: 'CTA',
    note: 'Straight into that clinician’s case in the existing experience.',
  },
]

export const pillar = {
  name: 'Pillar Teaser',
  concept: 'The Filtek Clinical Case Library',
  positioning: 'Built by Solventum. Made for clinicians.',
  question: 'How did they do that? Could I do that?',
  body: 'One Snak that sits above the whole series. It sells the concept of the library first — a growing repository of real cases, real decisions and real outcomes — so every clinician Teaser beneath it lands inside a bigger idea rather than as a one-off post.',
  screens: [
    { step: 'Screen 1', title: 'Hook', line: 'How did they do that?' },
    {
      step: 'Screen 2',
      title: 'The concept',
      line: 'A clinical case library, built by Solventum — for clinicians.',
    },
    {
      step: 'Screen 3',
      title: 'Interaction',
      line: 'Which challenge would you open first?',
    },
    { step: 'Screen 4', title: 'CTA', line: 'Could you do that? Enter the library.' },
  ],
  frames: [
    {
      id: 'open',
      image: 'https://cdn.magicpatterns.com/uploads/eT8iGjWv77A9pGLptXLZET/Screenshot_2026-09-08_at_12.17.04.jpg',
      label: 'Open',
      caption: 'One room. Many ways in.',
    },
    {
      id: 'question',
      image: 'https://cdn.magicpatterns.com/uploads/idZ9pqT1GWmswxFbiDyXF5/Screenshot_2026-09-08_at_12.17.36.jpg',
      label: 'The question',
      caption: '“Show me the prep.”',
    },
    {
      id: 'travel',
      image: 'https://cdn.magicpatterns.com/uploads/tAZt2nAqU1R2BFhFhM3rN7/Screenshot_2026-09-08_at_12.17.01.jpg',
      label: 'It travels',
      caption: 'Good cases travel — peer to peer.',
    },
    {
      id: 'destination',
      image: 'https://cdn.magicpatterns.com/uploads/2B82Hdyyhp1xuXnT5kEfPY/Screenshot_2026-09-08_at_12.17.17.jpg',
      label: 'The library',
      caption: 'Every case, credentialed and downloadable.',
    },
  ],
  roles: [
    {
      title: 'Pillar Teaser',
      body: 'Advertises the concept. Creates the category in the HCP’s mind: this is where clinical answers live.',
    },
    {
      title: 'Clinician Teasers',
      body: 'Answer the pillar question case by case, trading on each clinician’s authority, trust and credibility.',
    },
    {
      title: 'Clinical Case Library',
      body: 'The destination. Every Teaser — pillar or clinician — leads into the same growing repository.',
    },
  ],
}

export const flowSteps = [
  'Clinician',
  'Clinical challenge',
  'Interaction / question',
  'Tease the outcome',
  'Watch the full case',
]

export type Channel = {
  name: string
  detail: string
  icon:
    | 'linkedin'
    | 'email'
    | 'whatsapp'
    | 'qr'
    | 'congress'
    | 'booth'
    | 'rep'
    | 'digital'
}

export const channels: Channel[] = [
  { name: 'LinkedIn / social', detail: 'Clinician-led feed posts', icon: 'linkedin' },
  { name: 'Email', detail: 'One case per send', icon: 'email' },
  { name: 'WhatsApp', detail: 'Peer-to-peer sharing', icon: 'whatsapp' },
  { name: 'QR codes', detail: 'Print to Snak in one scan', icon: 'qr' },
  { name: 'Congress signage', detail: 'Different clinician per location', icon: 'congress' },
  { name: 'Booth materials', detail: 'Cards, screens, hand-outs', icon: 'booth' },
  { name: 'Sales-rep follow-up', detail: 'A case to leave behind', icon: 'rep' },
  { name: 'Digital campaigns', detail: 'Paid traffic with a reason to click', icon: 'digital' },
]

export const signals: string[] = [
  'Which clinicians attract the most attention',
  'Which clinical cases generate the highest CTR',
  'Which clinical questions create engagement',
  'How HCPs answer case-related polls and quizzes',
  'Which themes resonate most',
  'Which channels generate the strongest traffic',
  'Which congress placements generate engagement',
  'Which cases encourage HCPs to explore further',
]

export type SignalPanel = {
  id: string
  question: string
  metric: string
  rows: { label: string; value: number; display: string }[]
  action: string
}

export const signalPanels: SignalPanel[] = [
  {
    id: 'clinicians',
    question: 'Which clinician pulls hardest?',
    metric: 'Teaser → full case CTR',
    rows: [
      { label: "Dr O'Connor", value: 100, display: '4.8%' },
      { label: 'Dr Thering', value: 85, display: '4.1%' },
      { label: 'Dr Dobbertin', value: 71, display: '3.4%' },
      { label: 'Dr Bonacina', value: 60, display: '2.9%' },
    ],
    action: 'Put media weight behind the top case — and brief the next case in that voice.',
  },
  {
    id: 'answers',
    question: 'What did HCPs actually think?',
    metric: 'Case 01 poll — “What would influence your approach most?”',
    rows: [
      { label: 'Moisture control', value: 100, display: '41%' },
      { label: 'Access', value: 71, display: '29%' },
      { label: 'Adaptation', value: 51, display: '21%' },
      { label: 'Patient time', value: 22, display: '9%' },
    ],
    action: 'Lead the next Teaser — and the rep conversation — with moisture control.',
  },
  {
    id: 'channels',
    question: 'Where does the traffic come from?',
    metric: 'Share of qualified entries',
    rows: [
      { label: 'WhatsApp (peer share)', value: 100, display: '34%' },
      { label: 'LinkedIn', value: 79, display: '27%' },
      { label: 'Congress QR', value: 65, display: '22%' },
      { label: 'Email', value: 50, display: '17%' },
    ],
    action: 'Print more QR placements, and build every case to be forwardable.',
  },
]

export const modelSteps: { title: string; body: string }[] = [
  {
    title: 'Build once',
    body: 'One Teaser Master format built on the visual identity and interaction model of the existing Filtek clinical case campaign.',
  },
  {
    title: 'Create the series',
    body: 'Adapt the master around each clinician and clinical case. Same system, new story.',
  },
  {
    title: 'Activate everywhere',
    body: 'Social, email, WhatsApp, congress QR and sales teams — one Snak, many placements.',
  },
  {
    title: 'Learn',
    body: 'Measure traffic, interaction and clinical interest at case, question and channel level.',
  },
  {
    title: 'Optimize',
    body: 'Push the strongest cases, hooks and messages harder. Retire what does not travel.',
  },
]

export type ScopeLayer = {
  index: string
  title: string
  items: string[]
  price: string
  priceNote: string
  optional?: boolean
}

export const scopeLayers: ScopeLayer[] = [
  {
    index: '01',
    title: 'Teaser Master & Pillar Teaser',
    price: '€4,500',
    priceNote: 'One-off — includes the pillar Snak',
    items: [
      'Pillar Teaser Snak — “How did they do that?”',
      'Clinical Case Library positioning',
      'Creative concept',
      'Visual system',
      'Teaser structure',
      'Interaction logic',
      'CTA / journey into existing campaign',
      'Tracking architecture',
    ],
  },
  {
    index: '02',
    title: 'Clinician Teaser Series',
    price: '€2,500',
    priceNote: 'Per case — 02 and 03 combined',
    items: [
      'Clinician-led hook',
      'Clinical dilemma',
      'Interactive cue',
      'Case tease',
      'CTA into the full case',
    ],
  },
  {
    index: '03',
    title: 'Activation Toolkit',
    price: 'Included',
    priceNote: 'Combined with 02 at €2,500 per case',
    items: [
      'Social / email / WhatsApp links',
      'QR-ready campaign assets',
      'Congress activation assets',
      'Tracking by source, clinician and placement',
    ],
  },
  {
    index: '04',
    title: 'Country Activation & Support',
    price: '€850',
    priceNote: 'Per country activation',
    items: [
      '3 months of hosting',
      'Insight generation',
      'Campaign management',
      'Local performance reporting',
    ],
  },
]

export type CostLine = {
  label: string
  detail: string
  unit: string
  quantity: string
  amount: string
  optional?: boolean
}

export const costLines: CostLine[] = [
  {
    label: 'Teaser Master & Pillar Teaser (01)',
    detail:
      'Pillar Snak for the Clinical Case Library, plus creative concept, visual system, interaction logic and tracking architecture',
    unit: '€4,500',
    quantity: 'One-off',
    amount: '€4,500',
  },
  {
    label: 'Clinician Teaser Series + Activation Toolkit (02 / 03)',
    detail: 'One standalone Teaser per clinical case, with its activation assets',
    unit: '€2,500 per case',
    quantity: '4 cases',
    amount: '€10,000',
  },
  {
    label: 'Country Activation & Support (04)',
    detail: 'Includes 3 months hosting, insight generation and campaign management',
    unit: '€850 per country',
    quantity: '1 country',
    amount: '€850',
  },
  {
    label: 'Full campaign localization',
    detail: 'Optional — translation and local adaptation of the full campaign',
    unit: '€500 per country',
    quantity: 'Per country',
    amount: '€500',
    optional: true,
  },
]

export const costTotal = {
  label: 'Indicative total — 4 cases, 1 country activation',
  amount: '€15,350',
  note: 'Excluding optional localization. Additional countries: €850 activation (+€500 if localized).',
}

export const sections: { id: string; index: string; label: string }[] = [
  { id: 'cover', index: '01', label: 'Cover' },
  { id: 'opportunity', index: '02', label: 'The opportunity' },
  { id: 'idea', index: '03', label: 'The idea' },
  { id: 'pillar', index: '04', label: 'The pillar teaser' },
  { id: 'teaser', index: '05', label: 'The teaser' },
  { id: 'hook', index: '06', label: 'The hook' },
  { id: 'entry-points', index: '07', label: 'Many entry points' },
  { id: 'network', index: '08', label: 'Content network' },
  { id: 'signals', index: '09', label: 'Signals' },
  { id: 'model', index: '10', label: 'Campaign model' },
  { id: 'scope', index: '11', label: 'Proposed scope' },
]
